import type { JobCode } from 'core/API/scheduler';
import { getJobCodeDepartmentId } from 'module/schedule/utils/scheduleDepartmentUtils';

export type EligibleJobCodeRef =
	| number
	| string
	| { '@id'?: string; id?: number | string }
	| null
	| undefined;

export type EmployeeWithEligibleJobCodes = {
	eligibleJobCodes?: EligibleJobCodeRef[];
	_full?: { eligibleJobCodes?: EligibleJobCodeRef[] };
	[key: string]: unknown;
};

const parseNumericIdFromTail = (raw: string): number | null => {
	const match = raw.match(/(\d+)$/);
	if (match) return Number(match[1]);
	const n = Number(raw);
	return Number.isFinite(n) ? n : null;
};

/** Normalize a job-code ref (IRI, id, or embedded object) to a numeric id. */
export const parseJobCodeId = (value: unknown): number | null => {
	if (value == null) return null;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return null;
		return parseNumericIdFromTail(trimmed);
	}
	if (typeof value === 'object') {
		const ref = value as { '@id'?: string; id?: number | string };
		if (ref.id != null) {
			if (typeof ref.id === 'number' && Number.isFinite(ref.id)) return ref.id;
			if (typeof ref.id === 'string') {
				const trimmed = ref.id.trim();
				if (!trimmed) return null;
				return parseNumericIdFromTail(trimmed);
			}
		}
		if (typeof ref['@id'] === 'string') {
			const trimmed = ref['@id'].trim();
			if (!trimmed) return null;
			return parseNumericIdFromTail(trimmed);
		}
	}
	return null;
};

export const buildJobCodeIri = (jobCodeId: number): string => `api/job_codes/${jobCodeId}`;

/**
 * Collect numeric eligible job-code ids from a user (supports enriched workplace users
 * where eligibility may live on `_full`).
 */
export const getEligibleJobCodeIds = (user: EmployeeWithEligibleJobCodes | null | undefined): number[] => {
	if (!user) return [];
	const raw = user.eligibleJobCodes ?? user._full?.eligibleJobCodes ?? [];
	if (!Array.isArray(raw)) return [];
	const ids = new Set<number>();
	for (const ref of raw) {
		const id = parseJobCodeId(ref);
		if (id != null) ids.add(id);
	}
	return Array.from(ids);
};

/** An employee must have at least one eligible job code to be scheduleable. */
export const isEmployeeScheduleable = (user: EmployeeWithEligibleJobCodes | null | undefined): boolean =>
	getEligibleJobCodeIds(user).length > 0;

export const isEmployeeEligibleForJobCode = (
	user: EmployeeWithEligibleJobCodes | null | undefined,
	jobCodeId: number | string | null | undefined
): boolean => {
	if (jobCodeId == null || jobCodeId === '') return false;
	const targetId =
		typeof jobCodeId === 'number' ? jobCodeId : parseNumericIdFromTail(String(jobCodeId));
	if (targetId == null) return false;
	return getEligibleJobCodeIds(user).includes(targetId);
};

/**
 * True if the employee has any eligible job code that belongs to the given department.
 * `departmentId` null means "No Department" (job codes with no department).
 */
export const isEmployeeEligibleForDepartment = (
	user: EmployeeWithEligibleJobCodes | null | undefined,
	departmentId: number | null,
	jobCodes: JobCode[]
): boolean => {
	const eligibleIds = new Set(getEligibleJobCodeIds(user));
	if (eligibleIds.size === 0) return false;
	return jobCodes.some((jc) => {
		if (!eligibleIds.has(jc.id)) return false;
		return getJobCodeDepartmentId(jc) === departmentId;
	});
};

/**
 * Employees eligible to be assigned to a shift with the given job code.
 * Does not include House Shift sentinel — callers should prepend via withAssignedEmployeeOption.
 */
export const filterEmployeesForShiftAssignment = <T extends EmployeeWithEligibleJobCodes>(
	employees: T[],
	jobCodeId: number | string | null | undefined
): T[] => {
	if (jobCodeId == null || jobCodeId === '') return [];
	return employees.filter((employee) => isEmployeeEligibleForJobCode(employee, jobCodeId));
};

const isHouseShiftEmployee = (employee: unknown): boolean => {
	if (!employee || typeof employee !== 'object') return false;
	const ref = employee as { id?: unknown; '@id'?: unknown };
	return ref.id === 'HOUSE_SHIFT' || ref['@id'] === 'null';
};

/**
 * Job codes the employee is eligible to work.
 * House Shift / unset employee → all job codes.
 * `includeJobCodeId` keeps the current shift job code visible when editing even if
 * eligibility no longer includes it.
 */
export const filterJobCodesForEmployee = <T extends { id?: number; '@id'?: string }>(
	jobCodes: T[],
	employee: EmployeeWithEligibleJobCodes | null | undefined,
	options?: { includeJobCodeId?: number | string | { id?: number; '@id'?: string } | null }
): T[] => {
	if (!employee || isHouseShiftEmployee(employee)) {
		return jobCodes;
	}

	const eligibleIds = new Set(getEligibleJobCodeIds(employee));
	const includeId = parseJobCodeId(options?.includeJobCodeId ?? null);

	return jobCodes.filter((jobCode) => {
		const id = parseJobCodeId(jobCode);
		if (id == null) return false;
		if (eligibleIds.has(id)) return true;
		return includeId != null && id === includeId;
	});
};
